import React, { useEffect, useMemo, useState } from 'react';
import ExoForm from '../../ExoForm';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../../../api/api';
import useFetch from '../../../../api/useFetch';
import { CALENDAR_PROVIDERS } from '../../../../config/calendarProviders';

export const NewCalendar = ({
  onClose = () => {},
  onCreate = () => {},
  open,
  startData,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData } = useFetch('integrations');
  const [connectedProviders, setConnectedProviders] = useState([]);

  useEffect(() => {
    if (apiData) {
      const connected = apiData?.data
        .filter(
          (el) =>
            Object.keys(CALENDAR_PROVIDERS).includes(el.type) &&
            !el.actionRequired
        )
        .map((el) => el.type);
      setConnectedProviders(connected);
    }
  }, [apiData]);

  const [isLoading, setIsLoading] = useState(false);

  // Transform startData to convert null/undefined syncProvider to 'none'
  const transformedStartData = useMemo(() => {
    if (!startData) return startData;
    return {
      ...startData,
      syncProvider: startData.syncProvider || 'none',
    };
  }, [startData]);

  // Build sync provider options with "No sync" as first option
  const syncProviderOptions = useMemo(() => [
    { label: t('No sync'), value: 'none' },
    ...connectedProviders.map((providerType) => ({
      label: CALENDAR_PROVIDERS[providerType]?.name || providerType,
      value: providerType,
    })),
  ], [connectedProviders, t]);

  const fields = useMemo(() => [
    {
      label: t('Color'),
      key: 'color',
      type: 'color',
    },
    {
      label: t('Name'),
      key: 'name',
      type: 'text',
    },
    {
      label: t('Every employee should have this calendar'),
      key: 'isPublic',
      type: 'checkbox',
    },
    ...(connectedProviders.length > 0
      ? [
          {
            label: t('Sync to'),
            key: 'syncProvider',
            type: 'customSelect',
            options: syncProviderOptions,
            noUnselect: true,
            default: 'none',
          },
        ]
      : []),
  ], [t, connectedProviders, syncProviderOptions]);

  const validationSchema = Yup.object().shape({
    color: Yup.string().required('Is required'),
    name: Yup.string().required('Is required'),
  });

  function handleSubmit(data) {
    const isUpdate = data.id ? true : false;

    data.isPublic = data.isPublic ?? false;
    // Derive shouldSync from whether a provider is selected (not 'none')
    const hasProvider = data.syncProvider && data.syncProvider !== 'none';
    data.shouldSync = hasProvider;
    data.syncProvider = hasProvider ? data.syncProvider : null;

    DBRequest({
      config,
      path: isUpdate ? `calendars/${data.id}` : 'calendars',
      method: isUpdate ? API_PATCH(data) : API_POST(data),
      onResponse: (res) => onCreate(res.data),
      onLoading: setIsLoading,
    });
  }

  return (
    <ExoForm
      isLoading={isLoading}
      fields={fields}
      onSubmit={handleSubmit}
      onCancel={onClose}
      noDifferences
      startDataCollection={transformedStartData}
      header={startData?.id ? t('Edit Calendar') : t('New Calendar')}
      validationSchema={validationSchema}
    />
  );
};
